import React, { useCallback, useEffect, useState } from 'react'
import {
  PrismicFaqCategory,
  PrismicLinkType,
} from 'src/typings/generated/graphql'
import CustomLink from 'src/utils/customLink'
import BertholdTitle from 'src/components/atoms/bertholdTitle'
import FaqCategoryQuestion, {
  generateFaqCategoryQuestionId,
} from 'src/components/molecules/faqCategoryQuestion'
import { generateRandomId } from 'src/utils/domHelper'
import SearchIcon from 'src/images/svgs/search.svg'
import Fuse from 'fuse.js'

import * as GlobalStyles from 'src/styles/global.module.scss'
import * as Styles from './faqCategory.module.scss'

export type FaqCategoryContent = {
  anchor?: string
  headline?: string
  faqCategory?: PrismicFaqCategory
  link?: PrismicLinkType
  linkText?: string
}

type Props = {
  content: FaqCategoryContent
}

const FaqCategory = ({ content }: Props) => {
  const [searchCriteria, setSearchCriteria] = useState('')
  const [searchResults, setSearchResults]: any = useState([])
  const [searchResultsLength, setSearchResultsLength]: any = useState(
    `${searchResults?.length} FAQs found`
  )

  const {
    anchor = generateRandomId(),
    headline = '',
    faqCategory,
    link,
    linkText = '',
  } = content

  function handleSearchValue(event: React.ChangeEvent<HTMLInputElement>) {
    const searchValue = event.target.value
    setSearchCriteria(searchValue)
  }

  // Debounce function for screen reader text
  function debounce(func: any, wait: any) {
    let timeout: any
    // eslint-disable-next-line func-names
    return function (...args: any[]) {
      // @ts-ignore
      const context = this
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => {
        timeout = null
        func.apply(context, args)
      }, wait)
    }
  }

  const setResultsLength = (value: any) => {
    setSearchResultsLength(`${value?.length} FAQs found`)
  }

  const debounceOnChange = useCallback(debounce(setResultsLength, 1000), [])

  useEffect(() => {
    const formattedFaqs = faqCategory?.data?.question_and_answer?.map(
      (item: any) => ({
        item: {
          ...item,
        },
      })
    )
    setSearchResults(formattedFaqs)
  }, [])

  useEffect(() => {
    const options = {
      keys: ['question.text', 'answer.text', 'keyword_search_text.text'],
    }

    const formattedFaqs = faqCategory?.data?.question_and_answer?.map(
      (item: any) => ({
        item: {
          ...item,
        },
      })
    )

    const faqData: any = faqCategory?.data?.question_and_answer

    const fuse = new Fuse(faqData, options)

    // Clearing results on each search
    setSearchResultsLength('')

    if (searchCriteria === '') {
      setSearchResults(formattedFaqs)
      debounceOnChange(formattedFaqs)
    } else {
      const results = fuse.search(searchCriteria)
      setSearchResults(results)
      debounceOnChange(results)
    }
  }, [searchCriteria])

  return (
    <section id={anchor} className={Styles.faqCategory}>
      <div className={Styles.contentContainer}>
        <h2>{BertholdTitle({ text: headline })}</h2>
        <div className={Styles.searchContainer}>
          <input
            className={Styles.searchInput}
            type="text"
            value={searchCriteria}
            onChange={e => handleSearchValue(e)}
            placeholder="Search your question"
          />
          <SearchIcon className={Styles.searchIcon} />
        </div>
        <div className={Styles.questions}>
          <span className={GlobalStyles.srOnly} aria-live="assertive">
            {searchResultsLength}
          </span>
          {faqCategory &&
            searchResults?.map((qa: any, index: number) => {
              const questionId = generateFaqCategoryQuestionId(
                faqCategory.id,
                index
              )
              return (
                qa && (
                  <FaqCategoryQuestion
                    id={questionId}
                    question={qa.item}
                    key={questionId}
                  />
                )
              )
            })}
        </div>
        <CustomLink link={link} className={GlobalStyles.redBasicLink}>
          {linkText}
        </CustomLink>
      </div>
    </section>
  )
}

export default FaqCategory
